import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import ShareActions from '@/components/ShareActions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Globe, Link2, Loader2, CheckCircle2, AlertCircle,
  Sparkles, Copy, Code, ExternalLink, RefreshCw,
  Eye, TrendingUp, Zap, Shield
} from 'lucide-react';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function ConnectWebsite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [step, setStep] = useState(1); // 1: URL input, 2: Scanning, 3: Results, 4: Widget
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [savedWebsite, setSavedWebsite] = useState(null);
  const [existingWebsites, setExistingWebsites] = useState([]);
  const [copied, setCopied] = useState(false);
  const [kennel, setKennel] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/logowanie');
    } else if (user) {
      fetchExistingWebsites();
      fetchKennel();
    }
  }, [user, authLoading, navigate]);

  const fetchKennel = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/kennels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.length > 0) {
        setKennel(res.data[0]);
      }
    } catch (e) {
      // No kennel yet
    }
  };

  const fetchExistingWebsites = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/websites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingWebsites(res.data || []);
      
      // If user has existing website, show it
      if (res.data && res.data.length > 0) {
        setSavedWebsite(res.data[0]);
        setScanResults(res.data[0].scan_results);
        setStep(3);
      }
    } catch (e) {
      // No websites yet - that's ok
    }
  };

  const isValidUrl = (string) => {
    try {
      const url = new URL(string.startsWith('http') ? string : `https://${string}`);
      return url.hostname.includes('.');
    } catch {
      return false;
    }
  };

  const handleScan = async () => {
    if (!isValidUrl(url)) {
      toast.error('Wprowadź poprawny adres URL');
      return;
    }

    const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
    setScanning(true);
    setStep(2);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/websites/scan`, 
        { url: normalizedUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setScanResults(res.data.scan_results);
      setSavedWebsite(res.data.website);
      setStep(3);
      toast.success('Analiza zakończona!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd analizy strony');
      setStep(1);
    } finally {
      setScanning(false);
    }
  };

  const handleRescan = async () => {
    if (!savedWebsite) return;
    
    setScanning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/websites/${savedWebsite.id}/rescan`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setScanResults(res.data.scan_results);
      toast.success('Strona przeskanowana ponownie');
    } catch (e) {
      toast.error('Błąd skanowania');
    } finally {
      setScanning(false);
    }
  };

  const copyWidgetCode = () => {
    if (!savedWebsite) return;
    
    const widgetCode = `<!-- ApisAI Widget -->
<script src="https://apisai.pl/widget.js" data-site-id="${savedWebsite.id}"></script>
<div id="apisai-widget"></div>`;
    
    navigator.clipboard.writeText(widgetCode);
    setCopied(true);
    toast.success('Kod skopiowany!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 40) return 'text-[#F47A3B]';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-emerald-50 border-emerald-200';
    if (score >= 40) return 'bg-orange-50 border-[#F47A3B]/20';
    return 'bg-red-50 border-red-200';
  };

  if (authLoading) {
    return (
      <PanelLayout title="Połącz stronę">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout title="Moja strona" subtitle="Połącz istniejącą stronę z ApisAI">
      <div className="max-w-3xl mx-auto">
        
        {/* Step 1: URL Input */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-zinc-200 overflow-hidden"
          >
            <div className="p-6 border-b border-zinc-200 bg-gradient-to-r from-[#F47A3B]/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#F47A3B] flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-zinc-900">Połącz swoją stronę</h1>
                  <p className="text-zinc-500">Wprowadź URL swojej strony hodowli</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Adres URL Twojej strony
                </label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="np. www.moja-hodowla.pl"
                      className="pl-10 h-12 bg-zinc-50 border-zinc-200"
                      onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                    />
                  </div>
                  <Button 
                    onClick={handleScan}
                    disabled={!url.trim()}
                    className="h-12 px-6 bg-[#F47A3B] hover:bg-[#E06A2B] text-white"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analizuj
                  </Button>
                </div>
              </div>
              
              {/* Benefits */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 text-sm">AI Scan</p>
                    <p className="text-xs text-zinc-500">Sprawdzimy widoczność w AI</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 text-sm">Rekomendacje</p>
                    <p className="text-xs text-zinc-500">Konkretne porady</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <Code className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 text-sm">Widget</p>
                    <p className="text-xs text-zinc-500">Kod do osadzenia</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Scanning */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-zinc-200 p-8 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Analizuję stronę...</h2>
            <p className="text-zinc-500 mb-6">{url}</p>
            
            <div className="max-w-xs mx-auto space-y-3">
              <div className="flex items-center gap-3 text-sm text-zinc-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Sprawdzanie dostępności</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-600">
                <Loader2 className="w-4 h-4 text-[#F47A3B] animate-spin" />
                <span>Analiza treści dla AI</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <div className="w-4 h-4 rounded-full border-2 border-zinc-300" />
                <span>Generowanie rekomendacji</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Results */}
        {step === 3 && scanResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Score Card */}
            <div className={`rounded-2xl border p-6 ${getScoreBg(scanResults.overall_score)}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Globe className="w-6 h-6 text-zinc-600" />
                  <div>
                    <p className="font-medium text-zinc-900">{savedWebsite?.url}</p>
                    <p className="text-sm text-zinc-500">Ostatnia analiza: {new Date(savedWebsite?.last_scan || Date.now()).toLocaleDateString('pl-PL')}</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleRescan}
                  disabled={scanning}
                  className="border-zinc-300"
                >
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  <span className="ml-2 hidden sm:inline">Skanuj ponownie</span>
                </Button>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className={`text-5xl font-bold ${getScoreColor(scanResults.overall_score)}`}>
                    {scanResults.overall_score}%
                  </p>
                  <p className="text-sm text-zinc-500 mt-1">Widoczność AI</p>
                </div>
                
                <div className="flex-1">
                  <Progress 
                    value={scanResults.overall_score} 
                    className="h-3 bg-white/50"
                  />
                </div>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                <h2 className="font-semibold text-zinc-900">Analiza szczegółowa</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {scanResults.categories?.map((cat, idx) => (
                  <div key={idx} className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      cat.score >= 70 ? 'bg-emerald-50' : cat.score >= 40 ? 'bg-orange-50' : 'bg-red-50'
                    }`}>
                      {cat.score >= 70 ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <AlertCircle className={`w-5 h-5 ${cat.score >= 40 ? 'text-[#F47A3B]' : 'text-red-500'}`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-zinc-900">{cat.name}</p>
                      <p className="text-sm text-zinc-500">{cat.description}</p>
                    </div>
                    <Badge className={`${
                      cat.score >= 70 ? 'bg-emerald-50 text-emerald-600' : 
                      cat.score >= 40 ? 'bg-orange-50 text-[#F47A3B]' : 'bg-red-50 text-red-500'
                    }`}>
                      {cat.score}%
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {scanResults.recommendations?.length > 0 && (
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                  <h2 className="font-semibold text-zinc-900">Rekomendacje</h2>
                </div>
                <div className="p-4 space-y-3">
                  {scanResults.recommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-[#F7F3EE]">
                      <Zap className="w-5 h-5 text-[#F47A3B] flex-shrink-0 mt-0.5" />
                      <p className="text-zinc-700">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Widget Section */}
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="p-4 border-b border-zinc-200 bg-gradient-to-r from-purple-50 to-transparent">
                <div className="flex items-center gap-3">
                  <Code className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-zinc-900">Widget ApisAI</h2>
                </div>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-zinc-600 text-sm">
                  Dodaj ten kod do swojej strony, aby wyświetlić badge ApisAI i zwiększyć widoczność w AI.
                </p>
                
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl text-sm overflow-x-auto">
{`<!-- ApisAI Widget -->
<script src="https://apisai.pl/widget.js" 
  data-site-id="${savedWebsite?.id || 'YOUR_SITE_ID'}">
</script>
<div id="apisai-widget"></div>`}
                  </pre>
                  <Button
                    size="sm"
                    onClick={copyWidgetCode}
                    className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white"
                  >
                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Installation instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
                  <p className="font-medium text-blue-900 text-sm">Jak osadzić widget?</p>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Skopiuj powyższy kod (kliknij ikonę kopiowania)</li>
                    <li>Otwórz edytor swojej strony (WordPress, HTML, itp.)</li>
                    <li>Wklej kod tuż przed zamknięciem tagu <code className="bg-blue-100 px-1 rounded">&lt;/body&gt;</code></li>
                    <li>Zapisz zmiany i odśwież stronę</li>
                  </ol>
                  <p className="text-xs text-blue-600 mt-2">
                    Widget pojawi się w prawym dolnym rogu Twojej strony.
                  </p>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-zinc-500">
                  <Shield className="w-4 h-4" />
                  <span>Widget jest bezpieczny, asynchroniczny i nie spowalnia ładowania strony</span>
                </div>
              </div>
            </div>

            {/* Share Section - integrated ShareActions */}
            {kennel && kennel.slug && (
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                <div className="p-6">
                  <ShareActions
                    type="kennel"
                    slug={kennel.slug}
                    name={kennel.name}
                    city={kennel.city}
                    description={kennel.description}
                    boosterActive={false}
                  />
                </div>
              </div>
            )}

            {/* Back to add new */}
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => { setStep(1); setUrl(''); setScanResults(null); setSavedWebsite(null); }}
                className="border-zinc-200"
              >
                Dodaj inną stronę
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </PanelLayout>
  );
}
